import React, { useCallback } from 'react';
import { useEffect, useMemo } from 'react';
import { useNewUiTheme } from '../../newUiTheme';
import { useInputHelper } from './inputHelper';
import { WysiwygLayoutProps, WysiwygProps } from './Wysiwyg';
import { isReactNative } from '../../isReactNative';

export enum TagType {
  TAG = 'tag',
  MENTION = 'mention',
}

export type Tag = {
  id: string,
  value: string
  isDeprecated: boolean,
  type?: TagType,
}

export interface SmartTagsWysiwygLayoutProps extends WysiwygLayoutProps {
  availableTags: Tag[],
  hideHowToUseLink?: boolean,
}

interface SmartTagsWysiwygProps extends WysiwygProps {
  hideHowToUseLink?: boolean,
  availableTags: Tag[],
}

export function useSmartTagsUpdater(props: SmartTagsWysiwygProps) {

  const inputHelper = useInputHelper(props);

  const [version, setVersion] = React.useState(0);

  const updateSmartTags = useCallback((htmlString: string) => {
    const smartTagsMap: { [key: string]: string } = {};
    props.availableTags.forEach(tag => {
      smartTagsMap[tag.id] = tag.value;
    });

    let output = htmlString;

    if (!isReactNative()) {
      // React JS implementation
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const mentions = doc.querySelectorAll('span.mention');

      mentions.forEach(mention => {
        const tagId = mention.getAttribute('data-id');
        if (tagId && smartTagsMap[tagId]) {
          const newValue = smartTagsMap[tagId];
          mention.setAttribute('data-value', newValue);

          const contentEditableSpan = mention.querySelector('span');
          if (contentEditableSpan) {
            const qlMentionSpan = contentEditableSpan.querySelector('span');
            if (qlMentionSpan) {
              contentEditableSpan.removeChild(qlMentionSpan);
            }
            contentEditableSpan.textContent = newValue;
            const newQlMentionSpan = document.createElement('span');
            newQlMentionSpan.className = 'ql-mention-denotation-char';
            newQlMentionSpan.textContent = '#';
            contentEditableSpan.insertBefore(newQlMentionSpan, contentEditableSpan.firstChild);
          }
        } else {
          mention.setAttribute('data-existing', 'false');
        }
      });

      output = doc.body.innerHTML;

      output = output.trim();
    } else {
      const mentionPattern = /<span class="mention[^"]+?".+?data\Sid="([^"]+)".+?#<\/span>.+?<\/span>.?<\/span>/g;

      let match;

      while ((match = mentionPattern.exec(output)) !== null) {
        const tagId = match[1];

        const exists = props.availableTags.some(item => item.id === tagId);

        if (!exists) {
          // Replace the whole mention span with an empty string
          output = output.replace(match[0], '');
        }
      }

      return output;
    }

    return output;
  }, [props.availableTags]);

  useEffect(() => {
    const value = inputHelper.getValue();
    if (value && props.availableTags.length > 0) {
      const updatedValue = updateSmartTags(value);

      if (updatedValue !== value) {
        setTimeout(() => {
          inputHelper.getOnChangeCallback()({
            target: {
              value: updatedValue,
            },
          });
        });
      }

      setVersion(version + 1);
    }
  }, [props.availableTags.length]);

  return {
    version,
  };
}

export function SmartTagsWysiwyg(props: SmartTagsWysiwygProps) {
  const uiTheme = useNewUiTheme();

  const inputHelper = useInputHelper(props);

  useSmartTagsUpdater(props);

  const notDeprecatedTags = useMemo(
    () => props.availableTags.filter((tag) => !tag.isDeprecated),
    [props.availableTags.length],
  );

  return (
    <uiTheme.inputs.SmartTagsWysiwygLayout
      {...inputHelper.getBaseInputLayoutProps()}
      hideControls={props.hideControls}
      disableHotkeys={props.disableHotkeys}
      availableTags={notDeprecatedTags}
    />
  );
}
